import { useCallback, useEffect, useRef, useState } from 'react';
import type { PickedContact } from '../../../../lib/contact-references';

// The composer's selected-contacts state — the contacts picked via "+ Contact" that will ride the
// next send as `references.contacts`. De-dupes by contact id so tapping the same row twice is a no-op.
export function useContactReferences({
  pendingContacts,
  clearPendingContacts,
}: {
  // See `ActiveConversationContext.pendingContacts` — folded in on mount.
  pendingContacts?: PickedContact[] | null;
  clearPendingContacts?: () => void;
} = {}) {
  const [pickedContacts, setPickedContacts] = useState<PickedContact[]>([]);

  const addContact = useCallback((contact: PickedContact) => {
    setPickedContacts((prev) => (prev.some((p) => p.id === contact.id) ? prev : [...prev, contact]));
  }, []);

  const removeContact = useCallback((id: string) => {
    setPickedContacts((prev) => prev.filter((p) => p.id !== id));
  }, []);

  const clearContacts = useCallback(() => setPickedContacts([]), []);

  // The ref makes consumption idempotent against StrictMode's double-run, mirroring
  // use-attachments.ts's `consumedUploadsRef`.
  const consumedContactsRef = useRef<PickedContact[] | null>(null);
  useEffect(() => {
    if (!pendingContacts || pendingContacts.length === 0) return;
    if (consumedContactsRef.current === pendingContacts) return;
    consumedContactsRef.current = pendingContacts;
    setPickedContacts((prev) => {
      const next = [...prev];
      for (const c of pendingContacts) if (!next.some((p) => p.id === c.id)) next.push(c);
      return next;
    });
    clearPendingContacts?.();
  }, [pendingContacts, clearPendingContacts]);

  return { pickedContacts, addContact, removeContact, clearContacts };
}
